<template>
  <div class="data-request-container bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 min-h-screen">
    <div class="max-w-2xl mx-auto px-6 py-12 md:px-8 lg:py-16">

      <!-- Header -->
      <header class="mb-10">
        <h1 class="text-3xl font-bold mb-3 tracking-tight text-gray-900 dark:text-white">Data Request</h1>
        <p class="text-gray-600 dark:text-gray-400">
          Exercise your privacy rights by submitting a request below. We will respond to your request via email within the timeframe required by applicable law.
        </p>
      </header>

      <!-- Form Card -->
      <div class="bg-white/80 dark:bg-slate-900/80 backdrop-blur-sm shadow-xl rounded-2xl p-6 sm:p-8 border border-slate-200/50 dark:border-slate-700/50">

        <!-- Cooldown Message -->
        <div v-if="cooldownMessage" class="mb-6 p-4 bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200 rounded-lg">
          <div class="flex items-center gap-2">
            <svg class="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
            </svg>
            <span>{{ cooldownMessage }}</span>
          </div>
        </div>

        <!-- Request Type Selection -->
        <div class="mb-6">
          <label class="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Select Request Type</label>
          <div class="space-y-3">
            <label
              v-for="option in requestOptions"
              :key="option.value"
              :class="[
                'flex items-start p-4 rounded-lg border-2 cursor-pointer transition-all duration-200',
                selectedType === option.value
                  ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20'
                  : 'border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600'
              ]"
            >
              <input
                type="radio"
                :value="option.value"
                v-model="selectedType"
                class="mt-1 h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300"
              >
              <div class="ml-3">
                <span class="block font-medium text-gray-900 dark:text-white">{{ option.label }}</span>
                <span class="block text-sm text-gray-500 dark:text-gray-400 mt-1">{{ option.description }}</span>
              </div>
            </label>
          </div>
        </div>

        <!-- Correction Details (conditional) -->
        <div v-if="selectedType === 'correct'" class="mb-6">
          <label class="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
            What data needs correction?
          </label>
          <textarea
            v-model="correctionDetails"
            maxlength="500"
            rows="4"
            placeholder="Please describe what information is incorrect and what the correct information should be..."
            class="w-full px-4 py-3 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-colors duration-200 resize-none"
          ></textarea>
          <div class="text-right text-sm text-gray-500 dark:text-gray-400 mt-1">
            {{ correctionDetails.length }}/500 characters
          </div>
        </div>

        <!-- Email Display (locked) -->
        <div class="mb-6">
          <label class="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
            We will respond to
          </label>
          <div class="px-4 py-3 rounded-lg bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-gray-700 dark:text-gray-300">
            {{ userEmail }}
          </div>
          <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">
            This is the email associated with your account and cannot be changed.
          </p>
        </div>

        <!-- Submit Button -->
        <button
          @click="handleSubmit"
          :disabled="!canSubmit || isSubmitting"
          :class="[
            'w-full py-3 px-6 rounded-lg font-semibold transition-all duration-200',
            canSubmit && !isSubmitting
              ? 'bg-indigo-600 hover:bg-indigo-700 text-white cursor-pointer'
              : 'bg-slate-300 dark:bg-slate-700 text-slate-500 dark:text-slate-400 cursor-not-allowed'
          ]"
        >
          <span v-if="isSubmitting" class="flex items-center justify-center gap-2">
            <svg class="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Submitting...
          </span>
          <span v-else>Submit Request</span>
        </button>

        <!-- Info Note -->
        <p class="text-xs text-gray-500 dark:text-gray-400 mt-4 text-center">
          For authorized agent requests, please contact <a href="mailto:dpo@betterconnect.app" class="text-indigo-600 dark:text-indigo-400 hover:underline">dpo@betterconnect.app</a>
        </p>
      </div>

      <!-- Toast Notification -->
      <Transition
        enter-active-class="transition ease-out duration-300"
        enter-from-class="transform translate-y-4 opacity-0"
        enter-to-class="transform translate-y-0 opacity-100"
        leave-active-class="transition ease-in duration-200"
        leave-from-class="transform translate-y-0 opacity-100"
        leave-to-class="transform translate-y-4 opacity-0"
      >
        <div
          v-if="toast.show"
          :class="[
            'fixed bottom-6 left-1/2 -translate-x-1/2 px-6 py-3 rounded-lg shadow-lg z-50 max-w-md',
            toast.type === 'success'
              ? 'bg-green-600 text-white'
              : 'bg-red-600 text-white'
          ]"
        >
          <div class="flex items-center gap-2">
            <svg v-if="toast.type === 'success'" class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
            </svg>
            <svg v-else class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
            <span>{{ toast.message }}</span>
          </div>
        </div>
      </Transition>

      <!-- Delete Confirmation Modal -->
      <Transition
        enter-active-class="transition ease-out duration-200"
        enter-from-class="opacity-0"
        enter-to-class="opacity-100"
        leave-active-class="transition ease-in duration-150"
        leave-from-class="opacity-100"
        leave-to-class="opacity-0"
      >
        <div v-if="showDeleteConfirm" class="fixed inset-0 z-50 flex items-center justify-center p-4">
          <!-- Backdrop -->
          <div class="absolute inset-0 bg-black/50 backdrop-blur-sm" @click="showDeleteConfirm = false"></div>

          <!-- Modal -->
          <div class="relative bg-white dark:bg-slate-800 rounded-2xl shadow-2xl max-w-md w-full p-6 z-10">
            <div class="text-center">
              <!-- Warning Icon -->
              <div class="mx-auto flex items-center justify-center h-14 w-14 rounded-full bg-red-100 dark:bg-red-900/30 mb-4">
                <svg class="h-7 w-7 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path>
                </svg>
              </div>

              <h3 class="text-xl font-bold text-gray-900 dark:text-white mb-2">Confirm Deletion Request</h3>
              <p class="text-gray-600 dark:text-gray-400 mb-6">
                This will submit a request for permanent deletion of your account and all associated data. This action cannot be undone.
              </p>

              <div class="flex gap-3">
                <button
                  @click="showDeleteConfirm = false"
                  class="flex-1 py-2.5 px-4 rounded-lg border border-slate-300 dark:border-slate-600 text-gray-700 dark:text-gray-300 font-medium hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors duration-200"
                >
                  Cancel
                </button>
                <button
                  @click="confirmDelete"
                  :disabled="isSubmitting"
                  class="flex-1 py-2.5 px-4 rounded-lg bg-red-600 hover:bg-red-700 text-white font-medium transition-colors duration-200 disabled:opacity-50"
                >
                  <span v-if="isSubmitting">Submitting...</span>
                  <span v-else>Yes, Delete My Data</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </Transition>

    </div>
  </div>
</template>

<script>
import { redirectToLogin as beginLogin } from '@/config/auth';
import { backendUrl } from '@/config/backend';

export default {
  name: 'DataRequest',
  data() {
    return {
      selectedType: null,
      correctionDetails: '',
      isSubmitting: false,
      showDeleteConfirm: false,
      cooldownMessage: null,
      userEmail: 'Loading...',
      toast: {
        show: false,
        message: '',
        type: 'success'
      },
      requestOptions: [
        {
          value: 'access',
          label: 'Access my data',
          description: 'Request a copy of all personal data we have collected about you.'
        },
        {
          value: 'correct',
          label: 'Correct my data',
          description: 'Request correction of inaccurate personal information we have about you.'
        },
        {
          value: 'delete',
          label: 'Delete my data',
          description: 'Request permanent deletion of your account and all associated data.'
        }
      ]
    };
  },
  computed: {
    canSubmit() {
      if (!this.selectedType) return false;
      if (this.selectedType === 'correct' && this.correctionDetails.trim().length === 0) return false;
      if (this.isOnCooldown(this.selectedType)) return false;
      return true;
    }
  },
  methods: {
    isOnCooldown(type) {
      if (!type) return false;
      const key = `dataRequest_${type}_timestamp`;
      const lastSubmitted = localStorage.getItem(key);
      if (!lastSubmitted) return false;

      const cooldownMs = 24 * 60 * 60 * 1000; // 24 hours
      const elapsed = Date.now() - parseInt(lastSubmitted, 10);
      return elapsed < cooldownMs;
    },
    getCooldownRemaining(type) {
      const key = `dataRequest_${type}_timestamp`;
      const lastSubmitted = localStorage.getItem(key);
      if (!lastSubmitted) return null;

      const cooldownMs = 24 * 60 * 60 * 1000;
      const elapsed = Date.now() - parseInt(lastSubmitted, 10);
      const remaining = cooldownMs - elapsed;

      if (remaining <= 0) return null;

      const hours = Math.floor(remaining / (60 * 60 * 1000));
      const minutes = Math.floor((remaining % (60 * 60 * 1000)) / (60 * 1000));
      return `${hours}h ${minutes}m`;
    },
    updateCooldownMessage() {
      if (!this.selectedType) {
        this.cooldownMessage = null;
        return;
      }

      const remaining = this.getCooldownRemaining(this.selectedType);
      if (remaining) {
        this.cooldownMessage = `You recently submitted this type of request. Please wait ${remaining} before submitting again.`;
      } else {
        this.cooldownMessage = null;
      }
    },
    handleSubmit() {
      if (!this.canSubmit) return;

      if (this.selectedType === 'delete') {
        this.showDeleteConfirm = true;
      } else {
        this.submitRequest();
      }
    },
    confirmDelete() {
      this.showDeleteConfirm = false;
      this.submitRequest();
    },
    async submitRequest() {
      if (this.isSubmitting) return;

      this.isSubmitting = true;

      try {
        const token = localStorage.getItem('token');
        if (!token) {
          beginLogin({ originalUrl: window.location.href });
          return;
        }

        const payload = {
          requestType: this.selectedType
        };

        if (this.selectedType === 'correct') {
          payload.correctionDetails = this.correctionDetails.trim();
        }

        const response = await fetch(backendUrl('/data-request'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(payload)
        });

        const data = await response.json();

        if (response.ok) {
          // Set cooldown
          const key = `dataRequest_${this.selectedType}_timestamp`;
          localStorage.setItem(key, Date.now().toString());

          // Show success toast
          this.showToast('Your request has been submitted successfully. We will respond via email.', 'success');

          // Reset form
          this.selectedType = null;
          this.correctionDetails = '';
          this.cooldownMessage = null;
        } else {
          this.showToast(data.error || 'Failed to submit request. Please try again.', 'error');
        }
      } catch (error) {
        console.error('Error submitting data request:', error);
        this.showToast('Failed to submit request. Please try again.', 'error');
      } finally {
        this.isSubmitting = false;
      }
    },
    showToast(message, type) {
      this.toast.message = message;
      this.toast.type = type;
      this.toast.show = true;

      setTimeout(() => {
        this.toast.show = false;
      }, 5000);
    }
  },
  watch: {
    selectedType() {
      this.updateCooldownMessage();
    }
  },
  async mounted() {
    const token = localStorage.getItem('token');
    if (!token) {
      this.$router.push('/');
      return;
    }

    try {
      const response = await fetch(backendUrl('/user_info'), {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        this.userEmail = data.email || 'Not available';
      } else {
        this.userEmail = 'Not available';
      }
    } catch (error) {
      console.error('Error fetching user info:', error);
      this.userEmail = 'Not available';
    }
  }
};
</script>

<style scoped>
.data-request-container {
  min-height: 100vh;
}
</style>
